import type {
  RatingChip,
  RatingLane as RatingLaneKey,
} from "@nlc/shared-types";

import {
  chipsForStars,
  RATING_INSTRUCTION,
  RATING_LANE_META,
  tierForStars,
} from "@nlc/shared-types";
import { Star } from "lucide-react";
import { Button, ToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import {
  CHIP_BASE,
  CHIP_NEGATIVE,
  CHIP_POSITIVE,
  CHIP_SELECTED,
} from "@/components/chip-styles";
import {
  STAR_EMPTY_CLASSES,
  STAR_FILL_CLASSES,
} from "@/components/star-rating";

interface RatingLaneProps {
  chips: string[];
  isDisabled?: boolean;
  lane: RatingLaneKey;
  onStarsChange: (stars: number) => void;
  onToggleChip: (label: string) => void;
  selectedOnly?: boolean;
  stars: number;
}

export function RatingLane({
  chips,
  isDisabled,
  lane,
  onStarsChange,
  onToggleChip,
  selectedOnly,
  stars,
}: RatingLaneProps) {
  const { question, title } = RATING_LANE_META[lane];
  const allChips = chipsForStars(lane, stars);
  const visibleChips = selectedOnly
    ? allChips.filter((chip) => chips.includes(chip.label))
    : allChips;

  return (
    <div className="flex flex-col gap-sm">
      <div>
        <p className="text-md font-semibold text-text-primary">{title}</p>
        <p className="text-sm text-text-tertiary">{question}</p>
      </div>

      <StarRating
        isDisabled={isDisabled}
        laneLabel={title}
        onChange={onStarsChange}
        value={stars}
      />

      {stars > 0 && (
        <div className="flex flex-col gap-sm animate-in fade-in slide-in-from-top-1 duration-200">
          <p className="text-sm text-text-tertiary">
            {RATING_INSTRUCTION[tierForStars(stars)]}
          </p>
          {selectedOnly && chips.length === 0 ? (
            <p className="text-sm text-text-tertiary italic">
              No chips selected
            </p>
          ) : (
            <ChipGroup
              chips={visibleChips}
              isDisabled={isDisabled}
              onToggle={onToggleChip}
              selected={chips}
            />
          )}
        </div>
      )}
    </div>
  );
}

function StarRating({
  isDisabled,
  laneLabel,
  onChange,
  value,
}: {
  isDisabled?: boolean;
  laneLabel: string;
  onChange: (stars: number) => void;
  value: number;
}) {
  return (
    <div className="flex gap-xs">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= value;

        return (
          <Button
            aria-label={`${star} star${star > 1 ? "s" : ""} for ${laneLabel}`}
            aria-pressed={filled}
            className="outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand-solid rounded-sm cursor-pointer transition-transform duration-150 hover:scale-110 active:scale-95 disabled:cursor-default disabled:hover:scale-100"
            isDisabled={isDisabled}
            key={star}
            onPress={() => onChange(star)}
          >
            <Star
              className={twMerge(
                "size-7 transition-colors",
                filled
                  ? `${STAR_FILL_CLASSES} animate-feedback`
                  : STAR_EMPTY_CLASSES,
              )}
            />
          </Button>
        );
      })}
    </div>
  );
}

function ChipGroup({
  chips,
  isDisabled,
  onToggle,
  selected,
}: {
  chips: RatingChip[];
  isDisabled?: boolean;
  onToggle: (label: string) => void;
  selected: string[];
}) {
  return (
    <div className="flex flex-wrap gap-sm">
      {chips.map((chip) => {
        const isSelected = selected.includes(chip.label);

        return (
          <ToggleButton
            className={twMerge(
              CHIP_BASE,
              "px-lg py-xs animate-in fade-in zoom-in-95 duration-200",
              isSelected
                ? CHIP_SELECTED
                : chip.sentiment === "positive"
                  ? CHIP_POSITIVE
                  : CHIP_NEGATIVE,
            )}
            isDisabled={isDisabled}
            isSelected={isSelected}
            key={chip.label}
            onChange={() => onToggle(chip.label)}
          >
            {chip.label}
          </ToggleButton>
        );
      })}
    </div>
  );
}
